import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

import { Neutrals, Primary } from '../../color-palette/colors';
import { FOCUS_RING, FOCUS_RING_OFFSET } from '../../focusRing';
import { surfaceSx } from '../SurfaceCard';

const COARSE = '@media (pointer: coarse)';
const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

export const settingsRowSx: SystemStyleObject<Theme> = {
  paddingInline: 4,
  paddingBlock: 3,
  minBlockSize: (theme: Theme) => theme.controlHeight.small,
  [COARSE]: { minBlockSize: (theme: Theme) => theme.controlHeight.medium },
};

export const groupListSx: SystemStyleObject<Theme> = {
  ...surfaceSx,
  padding: 0,
  margin: 0,
  listStyle: 'none',
  display: 'block',
  inlineSize: '100%',
  overflow: 'clip',
  containerType: 'inline-size',
  '& > li + li, & > div + div': { borderBlockStart: `1px solid ${Neutrals[100]}` },
};

export const groupRootSx: SystemStyleObject<Theme> = { display: 'block', inlineSize: '100%', minInlineSize: 0 };

export const rowRootSx: SystemStyleObject<Theme> = { display: 'block', minInlineSize: 0 };

const rowHeaderSx = (hasLeading: boolean, hasTrail: boolean): SystemStyleObject<Theme> => ({
  ...settingsRowSx,
  display: 'grid',
  gridTemplateColumns: [hasLeading ? 'auto' : null, 'minmax(0, 1fr)', hasTrail ? 'auto' : null]
    .filter((track): track is string => track !== null)
    .join(' '),
  columnGap: 3,
  alignItems: 'center',
  inlineSize: '100%',
  boxSizing: 'border-box',
});

export const rowTargetSx = (theme: Theme): SystemStyleObject<Theme> => ({
  margin: 0,
  border: 0,
  font: 'inherit',
  color: 'inherit',
  textAlign: 'start',
  textDecoration: 'none',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  transition: theme.transitions.create('background-color', { duration: theme.transitions.duration.shortest }),
  '@media (hover: hover)': { '&:hover': { backgroundColor: Neutrals[10] } },
  '&:active': { backgroundColor: Neutrals[50] },
  '&:focus-visible': { outline: FOCUS_RING, outlineOffset: -FOCUS_RING_OFFSET },
  [REDUCED_MOTION]: { transition: 'none' },
});

export const rowTextSx: SystemStyleObject<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  minInlineSize: 0,
  overflowWrap: 'anywhere',
};

export const rowLabelSx: SystemStyleObject<Theme> = { color: Neutrals[900] };
export const stackedLabelSx: SystemStyleObject<Theme> = { color: Neutrals[600] };
export const stackedValueSx: SystemStyleObject<Theme> = { color: Neutrals[900], fontVariantNumeric: 'tabular-nums' };
export const rowDescriptionSx: SystemStyleObject<Theme> = { color: Neutrals[600] };
export const switchLabelSx = (theme: Theme): SystemStyleObject<Theme> => ({
  ...rowLabelSx,
  cursor: 'pointer',
  '@media (pointer: coarse)': { display: 'flex', alignItems: 'center', minBlockSize: theme.controlHeight.medium },
});

export const rowTrailSx: SystemStyleObject<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 2,
  flexShrink: 0,
  color: Neutrals[600],
};

export const inlineValueSx: SystemStyleObject<Theme> = {
  color: Neutrals[600],
  fontVariantNumeric: 'tabular-nums',
  textAlign: 'end',
};

export const rowChildrenSx: SystemStyleObject<Theme> = { paddingInline: 4, paddingBlockEnd: 3 };

export const rowSwitchSx = (theme: Theme): SystemStyleObject<Theme> => ({
  '& .MuiSwitch-track': { backgroundColor: Neutrals[500], opacity: 1 },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: Primary[500], opacity: 1 },
  '& .MuiSwitch-thumb': { backgroundColor: theme.palette.background.paper },
  '& .MuiSwitch-switchBase.Mui-focusVisible .MuiSwitch-thumb': {
    outline: FOCUS_RING,
    outlineOffset: FOCUS_RING_OFFSET,
  },
  '@media (pointer: coarse)': {
    overflow: 'visible',
    '& .MuiSwitch-input': { insetBlockStart: `calc(-1 * ${theme.spacing(1)})`, blockSize: `calc(100% + ${theme.spacing(2)})` },
  },
});

const HEADER_SX: Readonly<Record<`${boolean}-${boolean}`, SystemStyleObject<Theme>>> = {
  'true-true': rowHeaderSx(true, true),
  'true-false': rowHeaderSx(true, false),
  'false-true': rowHeaderSx(false, true),
  'false-false': rowHeaderSx(false, false),
};

export const rowHeaderSxOf = (hasLeading: boolean, hasTrail: boolean): SystemStyleObject<Theme> =>
  HEADER_SX[`${hasLeading}-${hasTrail}`];

const TARGET_HEADER_SX = {
  leading: [rowHeaderSx(true, true), rowTargetSx],
  plain: [rowHeaderSx(false, true), rowTargetSx],
} as const;

export const rowTargetHeaderSxOf = (hasLeading: boolean): (typeof TARGET_HEADER_SX)[keyof typeof TARGET_HEADER_SX] =>
  TARGET_HEADER_SX[hasLeading ? 'leading' : 'plain'];
